import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

function parseDateParts(value: string) {
  const [year, month, day] = value.split('-').map(Number)
  return { year, month: month - 1, day }
}

function toDateValue(year: number, month: number, day: number) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function parseTimeParts(value: string) {
  const [hour, minute] = value.split(':').map(Number)
  return { hour: hour || 0, minute: minute || 0 }
}

function toTimeValue(hour: number, minute: number) {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

function formatDisplayDate(value: string, locale: string) {
  const { year, month, day } = parseDateParts(value)
  return new Date(year, month, day).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function formatDisplayTime(value: string, locale: string) {
  const { hour, minute } = parseTimeParts(value)
  return new Date(2000, 0, 1, hour, minute).toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function SightingDateField({
  dateMode,
  selectedDate,
  today,
  onSelectToday,
  onSelectDate,
}: {
  dateMode: 'today' | 'custom'
  selectedDate: string
  today: string
  onSelectToday: () => void
  onSelectDate: (value: string) => void
}) {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const [draftDate, setDraftDate] = useState(selectedDate)
  const locale = i18n.resolvedLanguage || 'en'
  const { year, month } = parseDateParts(draftDate)
  const [viewYear, setViewYear] = useState(year)
  const [viewMonth, setViewMonth] = useState(month)

  useEffect(() => {
    if (!open) return
    setDraftDate(selectedDate)
    const parts = parseDateParts(selectedDate)
    setViewYear(parts.year)
    setViewMonth(parts.month)
  }, [open, selectedDate])

  const weeks = useMemo(() => {
    const first = new Date(viewYear, viewMonth, 1)
    const startOffset = (first.getDay() + 6) % 7
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate()
    const cells: Array<{ value: string; label: number } | null> = []
    for (let i = 0; i < startOffset; i += 1) cells.push(null)
    for (let day = 1; day <= daysInMonth; day += 1) {
      cells.push({
        value: toDateValue(viewYear, viewMonth, day),
        label: day,
      })
    }
    while (cells.length % 7 !== 0) cells.push(null)
    const rows: (typeof cells)[] = []
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7))
    return rows
  }, [viewMonth, viewYear])

  const monthLabel = new Date(viewYear, viewMonth, 1).toLocaleDateString(locale, {
    month: 'long',
    year: 'numeric',
  })

  function shiftMonth(delta: number) {
    const next = new Date(viewYear, viewMonth + delta, 1)
    setViewYear(next.getFullYear())
    setViewMonth(next.getMonth())
  }

  function cancel() {
    setOpen(false)
  }

  function confirm() {
    onSelectDate(draftDate)
    setOpen(false)
  }

  return (
    <fieldset className="report-section">
      <legend>{t('fields.date')}</legend>
      <div className="choice-pair">
        <button
          type="button"
          className={`choice-button${dateMode === 'today' ? ' selected' : ''}`}
          onClick={onSelectToday}
        >
          {dateMode === 'today' && <span aria-hidden="true">✓</span>}
          {t('sighting.today')}
        </button>
        <button
          type="button"
          className={`choice-button${dateMode === 'custom' ? ' selected' : ''}`}
          onClick={() => setOpen(true)}
        >
          {dateMode === 'custom' && <span aria-hidden="true">✓</span>}
          {dateMode === 'custom'
            ? formatDisplayDate(selectedDate, locale)
            : t('sighting.chooseDate')}
        </button>
      </div>
      {open && (
        <div className="datetime-overlay" role="dialog" aria-modal="true" aria-label={t('fields.date')}>
          <div className="datetime-overlay-panel">
            <h3>{t('sighting.chooseDate')}</h3>
            <div className="datetime-picker calendar-picker">
              <div className="calendar-header">
                <button type="button" className="btn" onClick={() => shiftMonth(-1)} aria-label={t('sighting.prevMonth')}>
                  ‹
                </button>
                <strong>{monthLabel}</strong>
                <button type="button" className="btn" onClick={() => shiftMonth(1)} aria-label={t('sighting.nextMonth')}>
                  ›
                </button>
              </div>
              <div className="calendar-weekdays">
                {Array.from({ length: 7 }, (_, index) => {
                  const label = new Date(2024, 0, 1 + index).toLocaleDateString(locale, {
                    weekday: 'narrow',
                  })
                  return <span key={label + index}>{label}</span>
                })}
              </div>
              <div className="calendar-grid">
                {weeks.map((week, weekIndex) =>
                  week.map((cell, dayIndex) => {
                    if (!cell) return <span key={`${weekIndex}-${dayIndex}`} />
                    const disabled = cell.value > today
                    const selected = cell.value === draftDate
                    return (
                      <button
                        key={cell.value}
                        type="button"
                        className={`calendar-day${selected ? ' selected' : ''}`}
                        disabled={disabled}
                        onClick={() => setDraftDate(cell.value)}
                      >
                        {cell.label}
                      </button>
                    )
                  }),
                )}
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="btn" onClick={cancel}>
                {t('actions.cancel')}
              </button>
              <button type="button" className="btn btn-primary" onClick={confirm}>
                {t('actions.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </fieldset>
  )
}

export function SightingTimeField({
  timeMode,
  selectedTime,
  disabledNow,
  onSelectNow,
  onSelectTime,
}: {
  timeMode: 'now' | 'custom'
  selectedTime: string
  disabledNow: boolean
  onSelectNow: () => void
  onSelectTime: (value: string) => void
}) {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const [draftTime, setDraftTime] = useState(selectedTime)
  const [clockMode, setClockMode] = useState<'hour' | 'minute'>('hour')
  const locale = i18n.resolvedLanguage || 'en'

  useEffect(() => {
    if (!open) return
    setDraftTime(selectedTime)
    setClockMode('hour')
  }, [open, selectedTime])

  const { hour, minute } = parseTimeParts(draftTime)
  const hour12 = hour % 12 || 12
  const isPm = hour >= 12

  function setFromParts(nextHour: number, nextMinute: number) {
    setDraftTime(toTimeValue(nextHour, nextMinute))
  }

  function setHour12(nextHour12: number) {
    const normalized = nextHour12 % 12
    setFromParts(isPm ? normalized + 12 : normalized, minute)
    setClockMode('minute')
  }

  function setMinute(nextMinute: number) {
    setFromParts(hour, nextMinute)
  }

  function togglePeriod() {
    setFromParts(isPm ? hour - 12 : hour + 12, minute)
  }

  const handAngle =
    clockMode === 'hour'
      ? ((hour12 % 12) / 12) * 360
      : (minute / 60) * 360

  function cancel() {
    setOpen(false)
  }

  function confirm() {
    onSelectTime(draftTime)
    setOpen(false)
  }

  return (
    <fieldset className="report-section">
      <legend>{t('fields.time')}</legend>
      <div className="choice-pair">
        <button
          type="button"
          className={`choice-button${timeMode === 'now' ? ' selected' : ''}`}
          disabled={disabledNow}
          onClick={onSelectNow}
        >
          {timeMode === 'now' && <span aria-hidden="true">✓</span>}
          {t('sighting.rightNow')}
        </button>
        <button
          type="button"
          className={`choice-button${timeMode === 'custom' ? ' selected' : ''}`}
          onClick={() => setOpen(true)}
        >
          {timeMode === 'custom' && <span aria-hidden="true">✓</span>}
          {timeMode === 'custom'
            ? formatDisplayTime(selectedTime, locale)
            : t('sighting.chooseTime')}
        </button>
      </div>
      {disabledNow && (
        <p className="field-help">{t('sighting.pastDateNeedsTime')}</p>
      )}
      {open && (
        <div className="datetime-overlay" role="dialog" aria-modal="true" aria-label={t('fields.time')}>
          <div className="datetime-overlay-panel">
            <h3>{t('sighting.chooseTime')}</h3>
            <div className="datetime-picker clock-picker">
              <div className="clock-mode-tabs">
                <button
                  type="button"
                  className={clockMode === 'hour' ? 'selected' : ''}
                  onClick={() => setClockMode('hour')}
                >
                  {String(hour12).padStart(2, '0')}
                </button>
                <span>:</span>
                <button
                  type="button"
                  className={clockMode === 'minute' ? 'selected' : ''}
                  onClick={() => setClockMode('minute')}
                >
                  {String(minute).padStart(2, '0')}
                </button>
                <button type="button" className="period-toggle" onClick={togglePeriod}>
                  {isPm ? t('sighting.pm') : t('sighting.am')}
                </button>
              </div>
              <div className="clock-face" aria-hidden="true">
                <div
                  className="clock-hand"
                  style={{ transform: `translate(-50%, -100%) rotate(${handAngle}deg)` }}
                />
                <div className="clock-center" />
                {(clockMode === 'hour'
                  ? [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]
                  : [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55]
                ).map((value, index) => {
                  const angle = (index / 12) * Math.PI * 2 - Math.PI / 2
                  const x = 50 + Math.cos(angle) * 38
                  const y = 50 + Math.sin(angle) * 38
                  const selected =
                    clockMode === 'hour' ? value === hour12 : value === minute
                  return (
                    <button
                      key={`${clockMode}-${value}`}
                      type="button"
                      className={`clock-number${selected ? ' selected' : ''}`}
                      style={{ left: `${x}%`, top: `${y}%` }}
                      onClick={() => {
                        if (clockMode === 'hour') setHour12(value)
                        else setMinute(value)
                      }}
                    >
                      {clockMode === 'hour' ? value : String(value).padStart(2, '0')}
                    </button>
                  )
                })}
              </div>
              <label className="clock-manual">
                <span>{t('sighting.manualTime')}</span>
                <input
                  type="time"
                  value={draftTime}
                  onChange={(event) => setDraftTime(event.target.value)}
                />
              </label>
            </div>
            <div className="form-actions">
              <button type="button" className="btn" onClick={cancel}>
                {t('actions.cancel')}
              </button>
              <button type="button" className="btn btn-primary" onClick={confirm}>
                {t('actions.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </fieldset>
  )
}
